import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import KpiCard from '../ui/KpiCard';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, Legend, BarChart, Bar } from 'recharts';

const backendUrl = 'http://localhost:3001/api';

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
    <strong>{label}</strong>
    <span>{value}</span>
  </div>
);

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
    <h3 style={{ marginTop: 0 }}>{title}</h3>
    {children}
  </div>
);

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={{ display: 'flex', flexDirection: 'column' }}>
    <div style={{ fontSize: 12, color: '#666' }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
  </div>
);

const WaveDashboard: React.FC = () => {
  const [summary, setSummary] = useState<any>(null);
  const [dateRange, setDateRange] = useState<{ start?: string; end?: string }>({});
  const [merchantId, setMerchantId] = useState('');
  const [series, setSeries] = useState<{ loanActivities: any[]; loans: any[] }>({ loanActivities: [], loans: [] });
  const [np30, setNp30] = useState<any>(null);
  const [np60, setNp60] = useState<any>(null);
  const [np90, setNp90] = useState<any>(null);
  const [top, setTop] = useState<any[]>([]);
  const [signals, setSignals] = useState<any>(null);

  useEffect(() => {
    axios.get(`${backendUrl}/metrics/summary`, { params: { ...dateRange, merchantId: merchantId || undefined } }).then(r => setSummary(r.data));
    axios.get(`${backendUrl}/metrics/timeseries`, { params: { ...dateRange, merchantId: merchantId || undefined } }).then(r => setSeries(r.data));
    axios.get(`${backendUrl}/metrics/nonperforming`, { params: { bucket: 30, merchantId: merchantId || undefined } }).then(r => setNp30(r.data));
    axios.get(`${backendUrl}/metrics/nonperforming`, { params: { bucket: 60, merchantId: merchantId || undefined } }).then(r => setNp60(r.data));
    axios.get(`${backendUrl}/metrics/nonperforming`, { params: { bucket: 90, merchantId: merchantId || undefined } }).then(r => setNp90(r.data));
    axios.get(`${backendUrl}/metrics/top-merchants`, { params: { ...dateRange, merchantId: merchantId || undefined } }).then(r => setTop(r.data));
  }, [dateRange, merchantId]);

  return (
    <div style={{ padding: 16 }}>
      <h2>WAVE Dashboard</h2>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <input type="date" value={dateRange.start || ''} onChange={(e) => setDateRange(s => ({ ...s, start: e.target.value }))} />
        <input type="date" value={dateRange.end || ''} onChange={(e) => setDateRange(s => ({ ...s, end: e.target.value }))} />
        <input placeholder="Merchant ID (optional)" value={merchantId} onChange={(e) => setMerchantId(e.target.value)} />
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: 20, 
        marginBottom: 24,
        alignItems: 'stretch'
      }}>
        <KpiCard 
          title="Total Disbursed" 
          value={summary ? `${Number(summary.totalDisbursed || 0).toLocaleString('en-ZW')} ZiG` : '—'} 
          color="#1565c0" 
          deltaPct={summary?.deltas?.disbursed} 
          subtitle="vs prev period"
          clickable={true}
          details={{
            type: 'disbursements',
            data: series.loanActivities?.map((v: any) => ({
              applicantName: `Daily Activity`,
              date: v.date,
              amount: v.disbursementAmount || 0
            })) || [],
            summary: {
              totalDisbursed: summary?.totalDisbursed,
              avgDisbursed: summary?.totalDisbursed && summary?.totalLoans ? summary.totalDisbursed / summary.totalLoans : 0,
              totalLoans: summary?.totalLoans
            }
          }}
        />
        <KpiCard 
          title="Total Repaid" 
          value={summary ? `${Number(summary.totalRepaid || 0).toLocaleString('en-ZW')} ZiG` : '—'} 
          color="#12b569"
          clickable={true}
          details={{
            type: 'repayments',
            data: series.loanActivities?.map((v: any) => ({
              applicantName: `Daily Activity`,
              date: v.date,
              amount: v.repaymentAmount || 0
            })) || [],
            summary: {
              totalRepaid: summary?.totalRepaid,
              repaymentRate: summary?.repaymentRate,
              onTimePayments: summary?.onTimePayments
            }
          }}
        />
        <KpiCard 
          title="Loan Applications" 
          value={summary ? Number(summary.totalApplications || 0) : '—'} 
          color="#1976d2" 
          deltaPct={summary?.deltas?.applications} 
          subtitle="vs prev period"
          clickable={true}
          details={{
            type: 'applications',
            data: series.loanActivities?.map((v: any) => ({
              applicantName: `Daily Activity`,
              submissionDate: v.date,
              requestedAmount: v.disbursementAmount || 0
            })) || [],
            summary: {
              totalApplications: summary?.totalApplications,
              pendingApplications: summary?.pendingApplications,
              avgApplicationAmount: summary?.avgApplicationAmount
            }
          }}
        />
        <KpiCard 
          title="Approved" 
          value={summary ? Number(summary.approvedLoans || 0) : '—'} 
          color="#00bcd4"
          clickable={true}
          details={{
            type: 'approved',
            data: [], // Will be populated with actual approved applications data
            summary: {
              approvedLoans: summary?.approvedLoans,
              totalApprovedAmount: summary?.totalApprovedAmount,
              approvalRate: summary?.approvalRate
            }
          }}
        />
        <KpiCard 
          title="Rejected" 
          value={summary ? Number(summary.rejectedLoans || 0) : '—'} 
          color="#ff4d4f"
          clickable={true}
          details={{
            type: 'rejected',
            data: [], // Will be populated with actual rejected applications data
            summary: {
              rejectedLoans: summary?.rejectedLoans,
              rejectionRate: summary?.rejectionRate,
              topRejectionReason: 'Credit Score'
            }
          }}
        />
        <KpiCard 
          title="30+ DPD" 
          value={np30 ? np30.count : '—'} 
          color="#ff8c00"
          clickable={true}
          details={{
            type: 'overdue',
            data: [], // Will be populated with actual overdue loans data
            summary: {
              overdue30Days: np30?.count,
              totalOverdueAmount: summary?.totalOverdueAmount,
              riskLevel: 'High'
            }
          }}
        />
      </div>

      <Card title="Daily Loan Activities">
        <div style={{ width: '100%', height: 260 }}>
          <ResponsiveContainer>
            <AreaChart data={series.loanActivities || []} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorDisbW" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1565c0" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#1565c0" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorRepayW" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#12b569" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#12b569" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis 
                tickFormatter={(value) => {
                  if (value >= 1000000) {
                    return `${(value / 1000000).toFixed(1)}M`;
                  } else if (value >= 1000) {
                    return `${(value / 1000).toFixed(1)}K`;
                  }
                  return value.toString();
                }}
              />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="disbursementAmount" stroke="#1565c0" fillOpacity={1} fill="url(#colorDisbW)" name="Disbursements" />
              <Area type="monotone" dataKey="repaymentAmount" stroke="#12b569" fillOpacity={1} fill="url(#colorRepayW)" name="Repayments" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Non-Performing Loans Section */}
      <div style={{ 
        background: 'linear-gradient(135deg, #ffffff 0%, #fef7f0 100%)', 
        borderRadius: 16, 
        padding: 24, 
        marginBottom: 24,
        border: '1px solid #fed7aa',
        boxShadow: '0 4px 20px rgba(251, 146, 60, 0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', margin: 0 }}>Non-Performing Loans</h3>
          <Link 
            to="/bank/np-loans"
            style={{
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: 8,
              textDecoration: 'none',
              fontSize: 14,
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(245, 158, 11, 0.3)';
            }}
          >
            View Details →
          </Link>
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{
            flex: '1 1 140px',
            background: 'linear-gradient(135deg, #fef2f2, #ffffff)',
            borderRadius: 12,
            padding: 16,
            border: '1px solid #fecaca',
            position: 'relative'
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#ef4444', borderRadius: '12px 12px 0 0' }} />
            <div style={{ fontSize: 12, color: '#991b1b', fontWeight: 600, marginBottom: 4 }}>30+ DAYS</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#dc2626' }}>{np30 ? np30.count : '—'}</div>
            <div style={{ fontSize: 11, color: '#7f1d1d', marginTop: 4 }}>Critical Risk</div>
          </div>
          <div style={{
            flex: '1 1 140px',
            background: 'linear-gradient(135deg, #fef3f2, #ffffff)',
            borderRadius: 12,
            padding: 16,
            border: '1px solid #fed7d7',
            position: 'relative'
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#f97316', borderRadius: '12px 12px 0 0' }} />
            <div style={{ fontSize: 12, color: '#c2410c', fontWeight: 600, marginBottom: 4 }}>60+ DAYS</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#ea580c' }}>{np60 ? np60.count : '—'}</div>
            <div style={{ fontSize: 11, color: '#9a3412', marginTop: 4 }}>High Risk</div>
          </div>
          <div style={{
            flex: '1 1 140px',
            background: 'linear-gradient(135deg, #fffbeb, #ffffff)',
            borderRadius: 12,
            padding: 16,
            border: '1px solid #fde68a',
            position: 'relative'
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#f59e0b', borderRadius: '12px 12px 0 0' }} />
            <div style={{ fontSize: 12, color: '#92400e', fontWeight: 600, marginBottom: 4 }}>90+ DAYS</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#d97706' }}>{np90 ? np90.count : '—'}</div>
            <div style={{ fontSize: 11, color: '#78350f', marginTop: 4 }}>Severe Risk</div>
          </div>
        </div>
      </div>

      {/* Top Merchants by Loan Disbursements */}
      <div style={{ 
        background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)', 
        borderRadius: 16, 
        padding: 24, 
        marginBottom: 24,
        border: '1px solid #bae6fd',
        boxShadow: '0 4px 20px rgba(59, 130, 246, 0.1)'
      }}>
        <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', marginBottom: 16 }}>Top Merchants by Loan Disbursements</h3>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={top || []} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis 
                tick={{ fontSize: 12, fill: '#64748b' }}
                tickFormatter={(value) => {
                  if (value >= 1000000) {
                    return `${(value / 1000000).toFixed(1)}M`;
                  } else if (value >= 1000) {
                    return `${(value / 1000).toFixed(1)}K`;
                  }
                  return value.toString();
                }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#ffffff', 
                  border: '1px solid #e2e8f0', 
                  borderRadius: 12,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                }} 
              />
              <Legend />
              <Bar 
                dataKey="totalDisbursed" 
                fill="url(#volumeGradient)" 
                name="Disbursements" 
                radius={[4, 4, 0, 0]}
              />
              <Bar 
                dataKey="totalRepaid" 
                fill="url(#txnGradient)" 
                name="Repayments"
                radius={[4, 4, 0, 0]}
              />
              <defs>
                <linearGradient id="volumeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.9}/>
                  <stop offset="95%" stopColor="#1976d2" stopOpacity={0.7}/>
                </linearGradient>
                <linearGradient id="txnGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.9}/>
                  <stop offset="95%" stopColor="#059669" stopOpacity={0.7}/>
                </linearGradient>
              </defs>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <Card title="Explore Loans">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Link to="/status" style={{ textDecoration: 'none', color: '#1565c0', fontWeight: 500 }}>
            📋 Application Status & Search
          </Link>
          <Link to="/transactions" style={{ textDecoration: 'none', color: '#1565c0', fontWeight: 500 }}>
            💰 Disbursement & Repayment Details
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default WaveDashboard;

